export const DATE_FORMATS = ["mdy", "dmy", "iso", "long"] as const;
export const TIME_FORMATS = ["none", "12h", "24h", "24h-seconds"] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];
export type TimeFormat = (typeof TIME_FORMATS)[number];

export const isDateFormat = (value: string | null): value is DateFormat =>
  value !== null && (DATE_FORMATS as readonly string[]).includes(value);
export const isTimeFormat = (value: string | null): value is TimeFormat =>
  value !== null && (TIME_FORMATS as readonly string[]).includes(value);

const pad = (value: number): string => String(value).padStart(2, "0");
export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Daily note filenames stay MM-DD-YYYY whatever the display date format is: changing a
 * preference must not start a second note for a day that already has one.
 */
export function dailyNoteName(date: Date): string {
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
}

/** A daily note's name with the hour and minute after it, for notes started several times a day. */
export function timedNoteName(date: Date): string {
  return `${dailyNoteName(date)} ${pad(date.getHours())}-${pad(date.getMinutes())}`;
}

export function formatToday(
  date: Date,
  dateFormat: DateFormat = "mdy",
  timeFormat: TimeFormat = "none",
): string {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const dateText = dateFormat === "dmy"
    ? `${pad(day)}/${pad(month)}/${year}`
    : dateFormat === "iso"
      ? `${year}-${pad(month)}-${pad(day)}`
      : dateFormat === "long"
        ? `${MONTH_NAMES[month - 1]} ${day}, ${year}`
        : `${pad(month)}/${pad(day)}/${year}`;
  if (timeFormat === "none") return dateText;
  const hour = date.getHours();
  const minute = pad(date.getMinutes());
  const timeText = timeFormat === "12h"
    ? `${hour % 12 || 12}:${minute} ${hour < 12 ? "AM" : "PM"}`
    : `${pad(hour)}:${minute}${timeFormat === "24h-seconds" ? `:${pad(date.getSeconds())}` : ""}`;
  return `${dateText} ${timeText}`;
}
